'use client';

import DOMPurify from 'isomorphic-dompurify';
import createCache, { type Options } from '@emotion/cache';
import { useServerInsertedHTML } from 'next/navigation';
import { CacheProvider } from '@emotion/react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import type { ReactNode } from 'react';
import React, { useMemo } from 'react';
import { theme } from '@packages/themes';
import type { Device } from '@packages/themes/src/default';
import { Snackbar, SnackbarProvider } from '@packages/shared';
import { NotificationProvider } from '@packages/shared/src/providers/NotificationProvider/NotificationProvider';

// This implementation is from emotion-js
// https://github.com/emotion-js/emotion/issues/2928#issuecomment-1319747902
export const ThemeRegistry = ({
  options,
  children,
  device,
}: {
  options: Options;
  children: ReactNode;
  device: Device;
}) => {
  const [{ cache, flush }] = React.useState(() => {
    // eslint-disable-next-line @typescript-eslint/no-shadow
    const cache = createCache(options);
    cache.compat = true;
    const prevInsert = cache.insert;
    let inserted: string[] = [];
    cache.insert = (...args) => {
      const serialized = args[1];
      if (cache.inserted[serialized.name] === undefined) {
        inserted.push(serialized.name);
      }
      return prevInsert(...args);
    };
    // eslint-disable-next-line @typescript-eslint/no-shadow
    const flush = () => {
      const prevInserted = inserted;
      inserted = [];
      return prevInserted;
    };
    return { cache, flush };
  });

  useServerInsertedHTML(() => {
    const names = flush();
    if (names.length === 0) {
      return null;
    }
    let styles = '';
    for (const name of names) {
      styles += cache.inserted[name];
    }
    return (
      <style
        key={cache.key}
        data-emotion={`${cache.key} ${names.join(' ')}`}
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(options.prepend ? `@layer emotion {${styles}}` : styles),
        }}
      />
    );
  });

  const memoizedTheme = useMemo(() => theme(device), [device]);

  return (
    <CacheProvider value={cache}>
      <ThemeProvider theme={memoizedTheme}>
        <SnackbarProvider>
          <NotificationProvider>
            <CssBaseline />
            <Snackbar />
            {children}
          </NotificationProvider>
        </SnackbarProvider>
      </ThemeProvider>
    </CacheProvider>
  );
};
